/**
 * WeeklyCloseoutBanner — Daniel 2026-07-20.
 *
 * On Monday (Tuesday grace day too) this dashboard strip prompts the
 * agent to close out LAST week's 12 Week Year check-in. Clicking opens a
 * review-and-confirm panel that lists last week's unfinished tasks,
 * pre-checked. Confirming submits last week and carries the checked
 * tasks into the new week.
 *
 * Self-hiding, like the other Home cards: renders null unless there's a
 * real last-week check-in that hasn't been submitted yet.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { closeWeek, getScorecard, getTwyCycle } from '../api/twy';

/** What happens to an unfinished task at closeout. */
type Disposition = 'done' | 'carry' | 'drop';

export default function WeeklyCloseoutBanner() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const userId = user?.id ?? 0;
  const isAgent = user?.portal === 'agent';

  const [open, setOpen] = useState(false);
  // Per-task disposition for the closeout (Daniel 2026-07-27): did it get
  // done, should it carry into next week, or is it dropped?
  const [choice, setChoice] = useState<Record<number, Disposition>>({});
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const cycleQ = useQuery({
    queryKey: ['twy-cycle'],
    queryFn: getTwyCycle,
    enabled: isAgent && userId > 0,
  });
  const currentWeek = cycleQ.data?.cycle?.current_week ?? 0;
  const totalWeeks = cycleQ.data?.cycle?.weeks ?? 0;
  const lastWeek = currentWeek - 1;

  const scQ = useQuery({
    queryKey: ['twy-scorecard', userId, lastWeek],
    queryFn: () => getScorecard(userId, lastWeek),
    enabled: isAgent && userId > 0 && lastWeek >= 1,
    retry: false,
  });

  // ── Gates (each renders null) ──────────────────────────────────────
  const day = new Date().getDay(); // 0 Sun .. 6 Sat
  if (!isAgent || dismissed) return null;
  if (day !== 1 && day !== 2) return null; // Monday, Tuesday grace only
  if (lastWeek < 1) return null;
  if (scQ.isLoading || scQ.isError || !scQ.data) return null;

  const sc = scQ.data.scorecard;
  if (sc.submitted_at) return null; // already closed out — nothing to do

  const unfinished = scQ.data.tasks.filter((t) => !t.done);
  // The next week is lastWeek + 1. Once the cycle has run past the final
  // week there's no next week to carry into, so we hide the carry list.
  const hasNextWeek = lastWeek < totalWeeks;

  function beginReview() {
    // Default every unfinished task to "carry" — the safe assumption.
    const next: Record<number, Disposition> = {};
    for (const t of unfinished) next[t.id] = 'carry';
    setChoice(next);
    setErr(null);
    setOpen(true);
  }

  async function confirm() {
    setSubmitting(true);
    setErr(null);
    try {
      const doneIds = unfinished.filter((t) => choice[t.id] === 'done').map((t) => t.id);
      const carryIds = hasNextWeek
        ? unfinished.filter((t) => choice[t.id] === 'carry').map((t) => t.id)
        : [];
      await closeWeek(sc.id, carryIds, doneIds);
      qc.invalidateQueries({ queryKey: ['twy-cycle'] });
      qc.invalidateQueries({ queryKey: ['twy-scorecard'] });
      setOpen(false);
      setDismissed(true);
    } catch (e) {
      setErr((e as Error).message || 'Could not close out the week.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        marginBottom: 24,
        background: 'var(--bg-warning, #FFF3D9)',
        borderLeft: '3px solid var(--border-warning, #B76B00)',
        borderRadius: 'var(--radius)',
        padding: '14px 18px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div
            style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text-warning, #B76B00)',
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}
          >
            12 Week Year
          </div>
          <div style={{ fontSize: 14, marginTop: 2, color: 'var(--text-body)' }}>
            Close out last week. Week {lastWeek} isn't submitted yet.
            {unfinished.length > 0
              ? ` ${unfinished.length} task${unfinished.length === 1 ? '' : 's'} still open.`
              : ' All tasks done.'}
          </div>
        </div>
        {!open && (
          <button
            type="button"
            onClick={beginReview}
            style={{
              fontSize: 12, fontWeight: 600, padding: '6px 14px',
              background: 'var(--brand-primary, #4f46e5)', color: '#fff',
              border: 'none', borderRadius: 6, cursor: 'pointer',
              fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            Review and close out
          </button>
        )}
      </div>

      {open && (
        <div
          style={{
            marginTop: 12, paddingTop: 12,
            borderTop: '1px solid var(--border-warning, #B76B00)',
          }}
        >
          {sc.mit && (
            <div style={{ fontSize: 13, marginBottom: 10, color: 'var(--text-body)' }}>
              <span style={{ fontWeight: 600 }}>Most important thing last week:</span> {sc.mit}
            </div>
          )}

          {hasNextWeek ? (
            unfinished.length > 0 ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-body)', marginBottom: 2 }}>
                  What happened with these?
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                  Done marks it complete for week {lastWeek}. Carry moves it into week {lastWeek + 1}.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {unfinished.map((t) => (
                    <div
                      key={t.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
                    >
                      <span style={{ flex: 1, color: 'var(--text-body)' }}>{t.title}</span>
                      {t.target_count > 1 && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {t.done_count}/{t.target_count}
                        </span>
                      )}
                      <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}>
                        {(['done', 'carry', 'drop'] as Disposition[]).map((d) => {
                          const active = (choice[t.id] ?? 'carry') === d;
                          const tone = d === 'done' ? '#1F7A3A' : d === 'carry' ? 'var(--brand-primary, #4f46e5)' : '#8A8A8A';
                          return (
                            <button
                              key={d}
                              type="button"
                              onClick={() => setChoice((p) => ({ ...p, [t.id]: d }))}
                              style={{
                                fontSize: 11, fontWeight: 600, padding: '3px 9px',
                                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                                background: active ? tone : 'transparent',
                                color: active ? '#fff' : 'var(--text-muted)',
                                textTransform: 'capitalize',
                              }}
                            >
                              {d}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                Nothing to carry over. Submitting finalizes the week.
              </div>
            )
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              This was the final week of the cycle. Submitting finalizes it.
            </div>
          )}

          {err && (
            <div style={{ color: 'var(--accent-red, #B71C1C)', fontSize: 13, marginTop: 10 }}>{err}</div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={confirm}
              disabled={submitting}
              style={{
                fontSize: 12, fontWeight: 600, padding: '7px 14px',
                background: 'var(--brand-primary, #4f46e5)', color: '#fff',
                border: 'none', borderRadius: 6, cursor: submitting ? 'wait' : 'pointer',
                fontFamily: 'inherit', opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting
                ? 'Submitting…'
                : (() => {
                    if (!hasNextWeek || unfinished.length === 0) return 'Submit week';
                    const d = unfinished.filter((t) => choice[t.id] === 'done').length;
                    const c2 = unfinished.filter((t) => (choice[t.id] ?? 'carry') === 'carry').length;
                    const bits = [d > 0 ? `${d} done` : '', c2 > 0 ? `${c2} carried` : ''].filter(Boolean);
                    return bits.length > 0 ? `Submit week · ${bits.join(', ')}` : 'Submit week';
                  })()}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              disabled={submitting}
              style={{
                fontSize: 12, fontWeight: 600, padding: '7px 14px',
                background: 'transparent', color: 'var(--text-body)',
                border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Not now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
